import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client.safe'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Users, Plus, X, Mail, Search } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

interface AuditMember {
  id: string
  user_id: string
  role: string
  added_at: string
  full_name?: string
  email?: string
  avatar_url?: string
}

interface UserProfile {
  id: string
  full_name?: string
  email?: string
  avatar_url?: string
}

interface MembersManagerProps {
  auditId?: string
  members: AuditMember[]
  onMembersChange: (members: AuditMember[]) => void
  currentUserId: string
  isOwner?: boolean
  organizationId?: string
}

export function MembersManager({ 
  auditId, 
  members, 
  onMembersChange, 
  currentUserId, 
  isOwner = false,
  organizationId 
}: MembersManagerProps) {
  const { toast } = useToast()
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<UserProfile[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedRole, setSelectedRole] = useState('member')

  const searchUsers = async (term: string) => {
    if (!term.trim() || term.length < 2) {
      setSearchResults([])
      return
    }

    if (!organizationId || organizationId === 'default-org') {
      setSearchResults([])
      return
    }

    setIsSearching(true)
    try {
      // Search for users within the same organization
      const { data, error } = await supabase
        .from('user_roles')
        .select(`
          user_id,
          profiles:user_id (
            id,
            full_name,
            email,
            avatar_url
          )
        `)
        .eq('organization_id', organizationId)
        .or(`full_name.ilike.%${term}%,email.ilike.%${term}%`, { foreignTable: 'profiles' })
        .limit(10)

      if (error) throw error

      // Transform data and filter out existing members
      const existingUserIds = members.map(m => m.user_id)
      const transformedResults = (data || [])
        .map(item => {
          const profile = Array.isArray(item.profiles) ? item.profiles[0] : item.profiles
          return profile as UserProfile
        })
        .filter(user => 
          user && 
          user.id &&
          !existingUserIds.includes(user.id) && 
          user.id !== currentUserId &&
          (user.full_name?.toLowerCase().includes(term.toLowerCase()) ||
           user.email?.toLowerCase().includes(term.toLowerCase()))
        )

      setSearchResults(transformedResults)
    } catch (error) {
      console.error('Error searching users:', error)
      toast({
        title: "Fel",
        description: "Kunde inte söka användare inom organisationen",
        variant: "destructive"
      })
    } finally {
      setIsSearching(false)
    }
  }

  useEffect(() => {
    const debounce = setTimeout(() => {
      searchUsers(searchTerm)
    }, 300)

    return () => clearTimeout(debounce)
  }, [searchTerm])

  const addMember = async (user: UserProfile) => {
    if (!auditId && !isOwner) {
      // For new case creation
      const newMember: AuditMember = {
        id: `temp_${Date.now()}_${user.id}`,
        user_id: user.id,
        role: selectedRole,
        added_at: new Date().toISOString(),
        full_name: user.full_name,
        email: user.email,
        avatar_url: user.avatar_url
      }
      onMembersChange([...members, newMember])
      setSearchTerm('')
      setSearchResults([])
      toast({
        title: "Medlem tillagd",
        description: `${user.full_name || user.email} har lagts till som ${selectedRole}`
      })
      return
    }

    // For existing audit
    try {
      const { data, error } = await supabase
        .from('audit_members')
        .insert({
          audit_id: auditId,
          user_id: user.id,
          role: selectedRole,
          added_by: currentUserId
        })
        .select(`
          id,
          user_id,
          role,
          added_at,
          profiles:user_id (
            full_name,
            email,
            avatar_url
          )
        `)
        .single()

      if (error) throw error

      const profileData = Array.isArray(data.profiles) ? data.profiles[0] : data.profiles
      const newMember: AuditMember = {
        id: data.id,
        user_id: data.user_id,
        role: data.role,
        added_at: data.added_at,
        full_name: profileData?.full_name,
        email: profileData?.email,
        avatar_url: profileData?.avatar_url
      }

      onMembersChange([...members, newMember])
      setSearchTerm('')
      setSearchResults([])
      
      toast({
        title: "Medlem tillagd",
        description: `${user.full_name || user.email} har lagts till som ${selectedRole}`
      })
    } catch (error) {
      console.error('Error adding member:', error)
      toast({
        title: "Fel",
        description: "Kunde inte lägga till medlem",
        variant: "destructive"
      })
    }
  }

  const removeMember = async (memberId: string, userId: string, userName: string) => {
    if (!auditId && !isOwner) {
      // For new case creation
      onMembersChange(members.filter(m => m.id !== memberId))
      toast({
        title: "Medlem borttagen",
        description: `${userName} har tagits bort från ärendet`
      })
      return
    }

    // For existing audit
    try {
      const { error } = await supabase
        .from('audit_members')
        .delete()
        .eq('id', memberId)

      if (error) throw error

      onMembersChange(members.filter(m => m.id !== memberId))
      
      toast({
        title: "Medlem borttagen",
        description: `${userName} har tagits bort från ärendet`
      })
    } catch (error) {
      console.error('Error removing member:', error)
      toast({
        title: "Fel",
        description: "Kunde inte ta bort medlem",
        variant: "destructive"
      })
    }
  }

  const getInitials = (name?: string, email?: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    }
    if (email) {
      return email[0].toUpperCase()
    }
    return '?'
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-primary text-primary-foreground'
      case 'admin':
        return 'bg-secondary text-secondary-foreground'
      case 'reviewer':
        return 'bg-accent text-accent-foreground'
      default:
        return 'bg-muted text-muted-foreground'
    }
  }

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner':
        return 'Ägare'
      case 'admin':
        return 'Admin'
      case 'reviewer':
        return 'Granskare'
      case 'member':
        return 'Medlem'
      default:
        return role
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Medlemmar ({members.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Members */}
        <div className="space-y-2">
          {members.map((member) => (
            <div key={member.id} className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center gap-3">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={member.avatar_url} />
                  <AvatarFallback>
                    {getInitials(member.full_name, member.email)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="font-medium text-sm">
                    {member.full_name || 'Namnlös användare'}
                  </div>
                  {member.email && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {member.email}
                    </div>
                  )}
                </div>
                <Badge className={getRoleColor(member.role)}>
                  {getRoleLabel(member.role)}
                </Badge>
              </div>
              {isOwner && member.role !== 'owner' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeMember(member.id, member.user_id, member.full_name || member.email || 'Användare')}
                  className="text-destructive hover:text-destructive"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>

        {isOwner && (
          <>
            <Separator />
            
            {/* Add Member */}
            <div className="space-y-4">
              <Label className="text-sm font-medium">Lägg till medlem</Label>
              
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Sök användare (namn eller e-post)..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Medlem</SelectItem>
                    <SelectItem value="reviewer">Granskare</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="border rounded-lg">
                  <div className="p-2 text-xs font-medium text-muted-foreground bg-muted/30">
                    Sökresultat
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {searchResults.map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-3 hover:bg-muted/50">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user.avatar_url} />
                            <AvatarFallback>
                              {getInitials(user.full_name, user.email)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium text-sm">
                              {user.full_name || 'Namnlös användare'}
                            </div>
                            {user.email && (
                              <div className="text-xs text-muted-foreground">
                                {user.email}
                              </div>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => addMember(user)}
                          className="gap-1"
                        >
                          <Plus className="h-3 w-3" />
                          Lägg till
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {searchTerm.length >= 2 && searchResults.length === 0 && !isSearching && (
                <div className="text-sm text-muted-foreground text-center py-4">
                  {!organizationId || organizationId === 'default-org' 
                    ? 'Ingen organisation konfigurerad för medlemssökning'
                    : `Inga användare hittades som matchar "${searchTerm}"`
                  }
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}